import { Loader2 } from 'lucide-react';

import { useState } from 'react';

import { DialogCloseButton } from '@/components/dialog-close';
import { COPY } from '@/shared/constants/copies';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';

import { KeyboardKeys, getCommandKeyboardKey } from '../utils/navigation';
import {
	ShortcutTooltipClient as ShortcutTooltip,
	ShortcutTooltipContentRenderer,
} from './shortcut-tooltip/shortcut-tooltip-client';

interface ConfirmDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => Promise<void> | void;
	description?: string;
	title?: string;
	variant?: 'delete' | 'default';
	confirmButtonText?: string;
	disableKeyboardShortcut?: boolean;
}

export default function ConfirmDialog({
	isOpen,
	onClose,
	onConfirm,
	description,
	title = COPY.confirms.areYouSure,
	variant = 'delete',
	confirmButtonText,
	disableKeyboardShortcut = false,
}: ConfirmDialogProps) {
	const isDeleteVariant = variant === 'delete';
	if (!confirmButtonText) confirmButtonText = isDeleteVariant ? 'Delete' : 'Confirm';

	const [isConfirming, setIsConfirming] = useState(false);

	const handleConfirm = async () => {
		setIsConfirming(true);
		await onConfirm();
		setIsConfirming(false);
		onClose();
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={onClose}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogCloseButton />
				</DialogHeader>
				<DialogDescription>{description}</DialogDescription>
				<DialogFooter className="mt-2">
					<Button
						variant="ghost"
						onClick={onClose}
						className="mr-2"
					>
						Cancel
					</Button>
					{disableKeyboardShortcut ? (
						<Button
							variant={isDeleteVariant ? 'destructive' : 'default'}
							onClick={handleConfirm}
						>
							{confirmButtonText}
							{isConfirming && <Loader2 className="size-4 animate-spin" />}
						</Button>
					) : (
						<ShortcutTooltip
							keys={[getCommandKeyboardKey(), KeyboardKeys.Enter]}
							hotKeyCallback={handleConfirm}
							hotKeyDependencies={[handleConfirm]}
							hotKeyOptions={{
								preventDefault: true,
								enableOnFormTags: false,
							}}
							skipTooltip
						>
							<Button
								variant={isDeleteVariant ? 'destructive' : 'default'}
								onClick={handleConfirm}
							>
								{confirmButtonText}

								{isConfirming ? (
									<Loader2 className="size-4 animate-spin" />
								) : (
									<ShortcutTooltipContentRenderer
										keys={[getCommandKeyboardKey(), KeyboardKeys.Enter]}
										small
										keysClassName="text-white"
									/>
								)}
							</Button>
						</ShortcutTooltip>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function ConfirmDialogContent({
	onClose,
	onConfirm,
	variant = 'delete',
	confirmButtonText,
	showProcessingWhileConfirming,
	disableKeyboardShortcut = false,
	reverseButtonOrder = false,
}: Omit<ConfirmDialogProps, 'isOpen' | 'title' | 'description'> & {
	showProcessingWhileConfirming?: boolean;
	/** Puts the confirm button first so it does not sit under the cursor after a
	 *  previous dialog's confirm — stops a double-click running both steps at once. */
	reverseButtonOrder?: boolean;
}) {
	const [isConfirming, setIsConfirming] = useState(false);
	const isDeleteVariant = variant === 'delete';
	if (!confirmButtonText) confirmButtonText = isDeleteVariant ? 'Delete' : 'Confirm';

	const handleConfirm = async () => {
		setIsConfirming(true);
		await onConfirm();
		setIsConfirming(false);
	};

	const confirmButton = (
		<Button
			variant={isDeleteVariant ? 'destructive' : 'default'}
			onClick={handleConfirm}
			disabled={isConfirming && showProcessingWhileConfirming}
		>
			{confirmButtonText}
			{isConfirming && showProcessingWhileConfirming ? (
				<Loader2 className="size-4 animate-spin" />
			) : !disableKeyboardShortcut ? (
				<ShortcutTooltipContentRenderer
					keys={[getCommandKeyboardKey(), KeyboardKeys.Enter]}
					small
					keysClassName="text-white"
				/>
			) : null}
		</Button>
	);

	const cancelButton = (
		<Button
			variant="ghost"
			onClick={onClose}
			className={reverseButtonOrder ? undefined : 'mr-2'}
		>
			Cancel
		</Button>
	);

	const confirmButtonWithShortcut = disableKeyboardShortcut ? (
		confirmButton
	) : (
		<ShortcutTooltip
			keys={[getCommandKeyboardKey(), KeyboardKeys.Enter]}
			hotKeyCallback={handleConfirm}
			hotKeyDependencies={[handleConfirm]}
			hotKeyOptions={{
				preventDefault: true,
				enableOnFormTags: false,
			}}
			skipTooltip
		>
			{confirmButton}
		</ShortcutTooltip>
	);

	return (
		<div>
			<div className="flex items-center justify-end gap-2">
				{reverseButtonOrder ? (
					<>
						{confirmButtonWithShortcut}
						{cancelButton}
					</>
				) : (
					<>
						{cancelButton}
						{confirmButtonWithShortcut}
					</>
				)}
			</div>
		</div>
	);
}
